import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { services } from '../mockData';

export const Services = () => {
  const { t } = useTranslation();
  
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Specialized Services
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Professional epoxy flooring solutions tailored to each project's needs
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.map((service) => (
              <Card key={service.id} className="overflow-hidden border-0 shadow-xl hover:shadow-2xl transition-all duration-300">
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/50 to-transparent"></div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <h2 className="text-3xl font-bold text-white mb-2">{service.title}</h2>
                  </div>
                </div>
                
                <CardContent className="p-8">
                  <p className="text-slate-600 text-lg mb-6">{service.description}</p>
                  
                  <div className="mb-6">
                    <h3 className="font-bold text-slate-800 mb-3 text-lg">Features:</h3>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {service.features.map((feature, idx) => (
                        <li key={`${service.id}-feature-${idx}`} className="flex items-start space-x-2">
                          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-slate-600">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Link to="/contacto">
                    <Button className="w-full bg-amber-600 hover:bg-amber-700 text-white">
                      Request Information
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-800 mb-4">
              Our Installation Process
            </h2>
            <p className="text-xl text-slate-600">
              A professional process guarantees exceptional results
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Evaluation',
                description: 'We inspect the area and determine the specific needs of the project'
              },
              {
                step: '02',
                title: 'Preparation',
                description: 'We prepare the surface through sanding, cleaning and crack repair'
              },
              {
                step: '03',
                title: 'Application',
                description: 'We apply high-quality epoxy layers with professional techniques'
              },
              {
                step: '04',
                title: 'Finishing',
                description: 'We seal and polish for a perfect and durable finish'
              }
            ].map((process) => (
              <div key={process.step} className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl font-bold text-white">{process.step}</span>
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-2">{process.title}</h3>
                <p className="text-slate-600">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-amber-500 to-orange-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Do You Have a Project in Mind?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Contact us today and get a personalized quote with no obligation
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/roi">
              <Button size="lg" className="bg-white text-amber-600 hover:bg-slate-50 text-lg px-8 py-6">
                Calculate Savings
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/contacto">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-amber-600 text-lg px-8 py-6">
                Contact Now
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
